'use client'

import * as React from 'react'

import { BREAKPOINTS } from '@/constant/breakpoints'
import { EVENTS } from '@/constant/siteConstants'

type PhotosListKeyboardControlsProps = {
  photosClassName: string
}

export default function PhotosListKeyboardControls({
  photosClassName,
}: PhotosListKeyboardControlsProps) {
  React.useEffect(() => {
    // Scroll to the next or previous photo
    const onNav = (el?: HTMLElement, direction?: 'next' | 'prev') => {
      if (!el) return
      const nextEl =
        direction === 'next' ? el.nextElementSibling : el.previousElementSibling
      // Scroll so the next el hits the top of the viewport
      if (nextEl) {
        const rect = nextEl.getBoundingClientRect()
        const scrollY = window.scrollY
        const top = rect.top + scrollY
        window.scrollTo({
          top,
          behavior: 'smooth',
        })
      }
    }

    // Get the current active photo
    const getActivePhoto = () => {
      const photoEls = document.querySelectorAll(
        `.${photosClassName}`,
      ) as NodeListOf<HTMLElement>

      // Stop if there are no photos
      if (!photoEls.length) return

      const viewportHeight = window.innerHeight

      const closePhotos = [] as {
        el: HTMLElement
        top: number
      }[]
      photoEls.forEach((photo) => {
        const { top } = photo.getBoundingClientRect()
        if (Math.abs(top) <= viewportHeight) {
          const el = photo as HTMLElement
          closePhotos.push({
            el,
            top: Math.abs(top),
          })
        }
        // Stop here when we've gone past the closest photo
        if (top > viewportHeight) return
      })

      // Sort closPhotos with highest top first
      const [activePhoto] = closePhotos.sort((a, b) => {
        if (a.top > b.top) return 1
        if (a.top < b.top) return -1
        return 0
      })
      return activePhoto
    }

    // Listen for keydown events
    const handleKeyDown = (event: KeyboardEvent) => {
      // Stop if the command key is down
      const isCommandKey = event.metaKey || event.ctrlKey
      if (isCommandKey) return

      // Stop if images aren't stack
      const isStacked = window.innerWidth < parseInt(BREAKPOINTS.sI)
      if (isStacked) return

      const isPrev = event.key === 'ArrowLeft'
      const isNext = event.key === 'ArrowRight'
      if (!isPrev && !isNext) return

      event.preventDefault()

      const activePhoto = getActivePhoto()

      const direction = isPrev ? 'prev' : 'next'
      onNav(activePhoto?.el, direction)
    }

    // Add key listeners
    window.addEventListener('keydown', handleKeyDown)

    // Add custom events listeners
    const onPrevEvent = () => {
      const activePhoto = getActivePhoto()
      onNav(activePhoto?.el, 'prev')
    }
    const onNextEvent = () => {
      const activePhoto = getActivePhoto()
      onNav(activePhoto?.el, 'next')
    }
    window.addEventListener(EVENTS.albumNext, onNextEvent)
    window.addEventListener(EVENTS.albumPrev, onPrevEvent)

    // Cleanup when component unmounts
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [photosClassName])
  return null
}
